import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { HelpCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { BackPill } from './BackPill';
import { useHistoryClose } from '../../hooks/useHistoryClose';
import { oathSealSfx, gratitudeRushSfx, laserLockSfx, crownSfx } from '../../utils/ritualSfx';
import { OBSIDIAN, PILLARS, REALMS, localDateKey } from './oath/oathTheme';
import { OathPact } from './oath/OathPact';
import { OathHits, kindOf } from './oath/OathHits';
import { OathLock } from './oath/OathLock';
import { GratitudeRush } from './oath/GratitudeRush';
import { OathIntro } from './oath/OathIntro';
import { RankBar } from './oath/RankBar';
import { OathCeremony } from './oath/OathCeremony';
import { DailyCrown } from './oath/DailyCrown';
import { RankUp } from './oath/RankUp';
import { LockInGame } from './oath/LockInGame';
export { OathTileIcon } from './oath/OathTileIcon';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/oath`;
const INTRO_KEY = 'oath_intro_seen_v1';
const errMsg = (e) => e?.response?.data?.detail || 'Something went wrong';

const PillarTabs = ({ tab, setTab, state }) => {
  const done = { pact: !!state?.today_pact, hits: state?.hits_today > 0, lock: state?.locked_today };
  return (
    <div className="grid grid-cols-3 gap-1.5 px-4 pt-3">
      {PILLARS.map((p) => {
        const on = tab === p.id;
        return (
          <button key={p.id} type="button" onClick={() => setTab(p.id)} data-testid={`oath-tab-${p.id}`} className="relative rounded-2xl py-2 transition-colors duration-300"
            style={{ border: `1px solid ${on ? p.accent : 'rgba(255,255,255,0.08)'}`, background: on ? `${p.accent}1A` : 'transparent', boxShadow: on ? `0 0 14px ${p.glow}` : 'none' }}>
            {done[p.id] && <span className="absolute top-1 right-2 text-[10px]" style={{ color: p.accent }}>✓</span>}
            <p className="font-['Cinzel'] text-[11px]" style={{ color: on ? p.light : 'rgba(255,255,255,0.35)' }}>{p.numeral}</p>
            <p className="font-['Outfit'] text-[9px] tracking-[0.12em] uppercase mt-0.5" style={{ color: on ? p.accent : 'rgba(255,255,255,0.4)' }}>{p.name}</p>
          </button>
        );
      })}
    </div>
  );
};

export const TheOath = ({ onClose }) => {
  const { user } = useAuth();
  const [tab, setTab] = useState('pact');
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [fx, setFx] = useState(null);
  const [intro, setIntro] = useState(() => { try { return !localStorage.getItem(INTRO_KEY); } catch { return false; } });
  useHistoryClose(true, useCallback(() => onClose(), [onClose]));
  const today = localDateKey();
  const pillar = PILLARS.find((p) => p.id === tab);

  useEffect(() => {
    axios.get(API, { params: { today } }).then(({ data }) => setState(data)).catch((e) => toast.error(errMsg(e)));
  }, [today]);

  const run = async (fn, after) => {
    setBusy(true);
    try {
      const { data } = await fn();
      const crowned = data.crown_today && !state?.crown_today;
      const promoted = state && data.rank !== state.rank && data.points > state.points;
      setState(data);
      after && after(data);
      if (crowned) pendingCrown.current = data.total_crowns;
      if (promoted) pendingRank.current = { rank: data.rank, points: data.points };
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const pendingCrown = useRef(null);
  const pendingRank = useRef(null);
  const clearFx = useCallback(() => setFx(null), []);
  /* Queue celebrations: crown first, then promotion — each waits for the current overlay. */
  useEffect(() => {
    if ((pendingCrown.current == null && !pendingRank.current) || (fx && fx.type !== 'rush')) return undefined;
    const t = setTimeout(() => {
      if (pendingCrown.current != null) {
        const total = pendingCrown.current;
        pendingCrown.current = null;
        crownSfx();
        setFx({ type: 'crown', total });
      } else {
        const r = pendingRank.current;
        pendingRank.current = null;
        crownSfx();
        setFx({ type: 'rank', ...r });
      }
    }, fx?.type === 'rush' ? 1900 : 400);
    return () => clearTimeout(t);
  }, [fx, state]);

  const sign = (text, realm) => run(() => axios.post(`${API}/pact`, { text, realm, date: today }), () => {
    oathSealSfx();
    setFx({ type: 'ceremony', text, realm: REALMS.find((r) => r.id === realm) });
  });
  const keep = (date, kept) => run(() => axios.post(`${API}/keep`, { date, kept }, { params: { today } }), () => {
    if (kept) { oathSealSfx(); toast.success('Promise honoured. The flame grows. 🔥'); } else toast('Honesty is strength. Rise again tomorrow.');
  });
  const log = (kind, text) => run(() => axios.post(`${API}/hit`, { kind, text, date: today }), (d) => {
    const m = kindOf(kind);
    gratitudeRushSfx(d.hits_today);
    setFx({ type: 'rush', n: d.hits_today, label: m.label, kind, color: m.color, key: Date.now() });
    setTimeout(() => setFx((f) => (f?.type === 'rush' ? null : f)), 1900);
  });
  const remove = (id) => run(() => axios.delete(`${API}/hit/${id}`, { params: { today } }));
  const finishGame = useCallback((score) => {
    const focus = fx?.focus;
    setFx(null);
    run(() => axios.post(`${API}/lock`, { date: today, focus, shattered: score }), () => {
      laserLockSfx();
      toast.success(`Focus locked · ${score} distractions shattered 💥`);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fx, today, state]);

  return (
    <div className="fixed inset-0 z-[220] flex flex-col overflow-hidden" data-testid="oath-modal"
      style={{ background: OBSIDIAN, paddingTop: 'max(var(--safe-area-inset-top, env(safe-area-inset-top, 0px)), 12px)' }}>
      <motion.div className="absolute inset-0 pointer-events-none" animate={{ background: `radial-gradient(ellipse at 50% -10%, ${pillar.glow.replace(/0\.\d+\)/, '0.25)')}, transparent 60%)` }} transition={{ duration: 0.6 }} />
      <div className="relative flex items-center justify-between px-3 py-2.5 border-b" style={{ borderColor: 'rgba(212,175,55,0.15)' }}>
        <BackPill onClick={onClose} accent="#D4AF37" testId="oath-close-btn" />
        <div className="text-center">
          <p className="font-['Cinzel'] tracking-[0.35em] text-[15px]" style={{ color: '#FFE9A0', textShadow: '0 0 12px rgba(212,175,55,0.45)' }}>OATH</p>
          <p className="font-['Outfit'] text-[8px] tracking-[0.3em] uppercase text-white/35">Rise stronger daily</p>
        </div>
        <button type="button" onClick={() => setIntro(true)} aria-label="What is Oath" data-testid="oath-help-btn" className="w-14 flex justify-end text-[#D4AF37]/70"><HelpCircle size={20} /></button>
      </div>
      {state && <RankBar state={state} />}
      <PillarTabs tab={tab} setTab={setTab} state={state} />
      <p className="relative px-6 pt-3 font-['Cormorant_Garamond'] italic text-sm text-center" style={{ color: `${pillar.light}B0` }}>{pillar.tagline}</p>
      <div className="relative flex-1 overflow-y-auto px-4 pt-4" style={{ paddingBottom: 'calc(var(--safe-area-inset-bottom, 0px) + 32px)' }}>
        {!state ? <p className="font-['Outfit'] text-xs text-white/40 text-center pt-16" data-testid="oath-loading">Opening the protocol…</p> : (
          <AnimatePresence mode="wait">
            <motion.div key={tab} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.25 }}>
              {tab === 'pact' && <OathPact state={state} onSign={sign} onKeep={keep} busy={busy} user={user} />}
              {tab === 'hits' && <OathHits state={state} onLog={log} onDelete={remove} busy={busy} />}
              {tab === 'lock' && <OathLock state={state} busy={busy} onStartGame={(focus) => setFx({ type: 'game', focus })} />}
            </motion.div>
          </AnimatePresence>
        )}
      </div>
      <AnimatePresence>
        {fx?.type === 'rush' && <GratitudeRush key={fx.key} count={fx.n} label={fx.label} kind={fx.kind} color={fx.color} />}
        {fx?.type === 'ceremony' && <OathCeremony key="cer" text={fx.text} realm={fx.realm} user={user} onDone={clearFx} />}
        {fx?.type === 'crown' && <DailyCrown key="crown" total={fx.total} onDone={clearFx} />}
        {fx?.type === 'rank' && <RankUp key="rank" rank={fx.rank} points={fx.points} onDone={clearFx} />}
        {fx?.type === 'game' && <LockInGame key="game" focus={fx.focus} onFinish={finishGame} onQuit={clearFx} />}
        {intro && <OathIntro key="intro" onStart={() => { try { localStorage.setItem(INTRO_KEY, '1'); } catch { /* ignore */ } setIntro(false); }} />}
      </AnimatePresence>
    </div>
  );
};

export default TheOath;
